import {
  Persona,
  Webinar,
  classifyWebinars,
  pickPersona,
} from './personaHelpers';

// Only the fields this hook reads off dashboard/v1/summary's
// `csoc_setup_dashboard` -- that object carries a lot more (setup-card
// fields gated by `show`), but is_master_class/webinar_list/
// past_webinar_list are always populated for any CSOC registration
// regardless of `show`, which is all persona resolution needs.
export interface CsocCurriculum {
  is_master_class: boolean;
  webinar_list: Webinar[];
  past_webinar_list: Webinar[];
}

/**
 * Resolves the dashboard persona purely from the signed-in user's real data:
 *
 *   player_type === 1                     -> first_timer
 *   has a class today (webinar_date)      -> csoc_class_day
 *   has an upcoming class                 -> csoc_rest_day
 *   otherwise                             -> self_learner
 *
 * "CSOC" is never a backend flag — it's inferred from the user actually
 * having webinars in dashboard/v1/summary's
 * `csoc_setup_dashboard.webinar_list`.
 *
 * Data comes straight from the summary payload Dashboard already has in
 * hand — the chess backend builds it in dashboard/csoc_setup.py
 * (get_csoc_setup_dashboard) from the student's real scheduled webinars —
 * so there's no separate fetch and no loading state here: by the time
 * Dashboard's body renders, summary data (and therefore this) is already
 * final.
 */
export default function usePersona(
  isBeginnerPlayer: boolean,
  curriculum?: CsocCurriculum | null,
) {
  const webinarList = curriculum?.webinar_list ?? [];
  const pastWebinarList = curriculum?.past_webinar_list ?? [];
  const isMasterClass = !!curriculum?.is_master_class;

  const classes = classifyWebinars(webinarList);
  const persona: Persona = pickPersona(!!isBeginnerPlayer, classes);

  return {
    persona,
    classes,
    webinarList,
    pastWebinarList,
    isMasterClass,
    // Kept for the caller's existing personaReady/RailSkeleton wiring —
    // always false now since persona resolves synchronously from data
    // Dashboard already has, rather than a separate async fetch.
    resolving: false,
  };
}
